import * as React from 'react';
import { Button, Icon, Tooltip } from 'kui';
import { ECardProgressSize, ICardProgressProps } from './types';
import './_CardProgress.scss';
import { CARD_CONTROL_CLASS } from '../../../../main/kanbanView';
import {
    CARD_PROGRESS_COLOR_BG,
    CARD_PROGRESS_COLOR_SUCCESS,
    CARD_PROGRESS_DARK_BACKGROUND_COLORS,
    CARD_PROGRESS_LIGHT_BACKGROUND_COLORS,
    CARD_PROGRESS_LIGHT_COLOR_BG,
} from './constants';
import { CLASS_GANTT_CARD_CONTROL } from '../../../../gantt/listCard/components/constants';
import { CLASS_LIST_VIEW_CARD_CONTROL } from '../../../../listView/containers/Card/constants';

export function CardProgress({
    backgroundColor,
    canDone,
    isButton,
    isColumnView,
    isLightBgStroke,
    isReadonly,
    progress,
    size = ECardProgressSize.BASE,
    tooltip,
    onClick,
    onShowBoardLimitSnackbarIfNeeded,
}: ICardProgressProps) {
    const className = 'card-progress';
    const classText = className + '__text';
    const classButton = className + '__button';
    const classButtonText = classButton + '-text';
    const classButtonIcon = className + '-icon';

    const [isChange, setChange] = React.useState(null);
    const [value, setValue] = React.useState(progress);
    const [isHidden, setIsHidden] = React.useState(null);
    const [scroll, setScroll] = React.useState(0);
    const [isDisabled, setDisabled] = React.useState(null);

    const initialRef = React.useRef(null);

    const isDone = progress === 100;
    const isEmpty = !progress;
    const isAction = !!onClick && !isReadonly && canDone;

    if (isEmpty) progress = 0;

    const handleClick = () => {
        if (isButton) return;
        if (!isReadonly) {
            onClick();
        }
    };

    const circleSize = size === ECardProgressSize.LARGE ? 32 : 24;
    const strokeWidth = 2;
    const radius = (circleSize - strokeWidth) / 2;
    const cx = circleSize / 2;
    const cy = circleSize / 2
    const circumference = radius * Math.PI * 2;
    const dash = (progress * circumference) / 100;

    const isLightBackground = CARD_PROGRESS_LIGHT_BACKGROUND_COLORS.includes(backgroundColor);
    const isDarkBackground = CARD_PROGRESS_DARK_BACKGROUND_COLORS.includes(backgroundColor);

    React.useEffect(() => {
        // не показывать анимацию в первый рендер
        if (initialRef.current) {
            setChange(true);
            setTimeout(() => {
                setValue(progress);
                setChange(false);
            }, 300); // transition length
        }

        initialRef.current = true;
    }, [progress]);

    const setOverall = () => {
        const body = document.querySelector(`${isColumnView ? '.card-details__container' : '.aside-panel__body'}`) as HTMLElement;
        const cardDetails = document.querySelector(`${isColumnView ? '.card-details__container' : '.card-details'}`) as HTMLElement;
        if (!isHidden) {
            setScroll(body.scrollTop);
            if (cardDetails) {
                const sections = cardDetails.querySelectorAll(':scope > *');
                sections.forEach((section: HTMLElement) => {
                    const height = section.offsetHeight;
                    section.style.height = height + 'px';
                });
                body.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                setTimeout(() => {
                    sections.forEach((section: HTMLElement) => {
                        section.style.height = 'unset';
                    });
                }, 600) //время анимации
            }
        } else {
            if (cardDetails) {
                requestAnimationFrame(() => {
                    body.scrollTo({
                        top: scroll,
                        behavior: 'smooth'
                    });
                })
            }
        }
        if (cardDetails && !isAction) {
            cardDetails.classList.add('card-details__overall');
            setIsHidden(true);
        }
        if (cardDetails && isHidden) {
            cardDetails.classList.remove('card-details__overall');
            setIsHidden(false);
        }
    }

    const CheckIcon = require('!!@svgr/webpack!./check-icon.svg').default;

    const progressElement = <div
        className={`
                ${className}__wrapper
                ${isHidden ? classButtonIcon + '-hidden' : ''}
            `}
        onClick={onShowBoardLimitSnackbarIfNeeded}>
        <div
            tabIndex={0}
            className={`
                        ${className}
                        ${CARD_CONTROL_CLASS}
                        ${CLASS_GANTT_CARD_CONTROL}
                        ${CLASS_LIST_VIEW_CARD_CONTROL}
                        ${className + '--' + size}
                        ${isLightBackground ? className + '--light-bg' : ''}
                        ${isDarkBackground ? className + '--dark-bg' : ''}
                        ${isEmpty ? className + '--empty' : ''}
                        ${isDone ? className + '--done' : ''}
                        ${isAction ? className + '--action' : ''}
                    `}
            onClick={handleClick}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.stopPropagation();
                    handleClick();
                }
            }}
        >
            <svg
                width={circleSize}
                height={circleSize}
                viewBox={`0 0 ${circleSize} ${circleSize}`}
            >
                <circle
                    fill={'none'}
                    stroke={isLightBgStroke ? CARD_PROGRESS_LIGHT_COLOR_BG : CARD_PROGRESS_COLOR_BG}
                    strokeWidth={`${strokeWidth}px`}
                    cx={cx}
                    cy={cy}
                    r={radius}
                />
                <circle
                    fill={'none'}
                    stroke={CARD_PROGRESS_COLOR_SUCCESS}
                    strokeWidth={`${strokeWidth}px`}
                    cx={cx}
                    cy={cy}
                    r={radius}
                />
                <circle
                    fill={'none'}
                    stroke={CARD_PROGRESS_COLOR_SUCCESS}
                    cx={cx}
                    cy={cy}
                    r={radius}
                    strokeWidth={`${strokeWidth}px`}
                    transform={`rotate(-90 ${circleSize / 2} ${circleSize / 2})`}
                    //@ts-ignore
                    strokeDasharray={[dash, circumference - dash]}
                />
            </svg>
            <div
                className={`
                        ${classText}
                        ${isChange ? classText + '--change' : ''}
                    `}
            >
                <CheckIcon className={className + '__icon'} />
                <div className={className + '__percent'}>
                    {value}
                </div>
            </div>
        </div>
    </div>

    React.useEffect(() => {
        if (!isHidden) return;
        const asidePanel = document.querySelector('.aside-panel__body--details') as HTMLElement;
        function onAsidePanelButton (e: MouseEvent) {
            const target = e.target as HTMLElement;
            if (target.closest('.aside-panel__actions-button')) {
                setOverall();
            }
        }
        if (asidePanel) asidePanel.addEventListener('click', onAsidePanelButton);

        return () => {
            if (asidePanel) asidePanel.removeEventListener('click', onAsidePanelButton);
        }
    }, [isHidden])

    return (!isButton ?
        <Tooltip
           isNoWrap
            isNoEvents
            value={tooltip}
        >
            {progressElement}
        </Tooltip> :
        <Button
            className={`
                    ${classButton}
                    ${isDone ? classButton + '--done' : ''}
                    ${isHidden ? classButton + '--close' : ''}
                    ${isDisabled ? classButton + '--disabled' : ''}
                `}
            variant={'icon-text'}
            onClick={() => {
                if (isAction) {
                    setDisabled(true);
                    if (!isHidden) onClick().finally(() => setDisabled(false));
                } else {
                    setOverall();
                }
            }}
            disabled={isDisabled}
        >
            {progressElement}
            <div
                className={`
                    ${classButtonText}
                    ${isDone ? classButtonText + '--done' : ''}
                `}>
                {tooltip}
            </div>
            {isHidden &&
                <Icon
                    className={`
                        ${classButtonIcon}-close
                    `}
                    size={24}
                    xlink={'close'}
                />
            }
        </Button>
    );
}
